import { ConfigurationResponse } from '@/services/models/ConfigurationResponse'
import { ProjectOverviewResponse } from '@/services/models/ProjectOverviewResponse'
import ProjectDataModel from '@/services/models/ProjectDataModel'
import { ProjectResponse } from '@/services/models/ProjectResponse'
import { ProductResponse } from '@/services/models/ProductResponse'
import {
  AvailableConfigsType,
  AvailableTranslationType,
  BusEvent,
  CharacteristicType,
  DownloadExcelType,
  InstallationType,
  ProductType,
  ReportVariant,
  SortDirection
} from '@/helper/Types'
import { SchemeResponse } from '@/services/models/SchemeResponse'
import { SchemeRequest } from '@/services/models/SchemeRequest'
import userStore from '@/store/UserStore'
import { CalculatedCoolingLoadRequest } from '@/services/models/CalculatedCoolingLoad'
import { CalculatedHeatingLoadRequest } from '@/services/models/CalculatedHeatingLoad'
import projectStore from '@/store/ProjectStore'
import { buildURLQueryParams } from '@/helper/tools'
import { KeycloakConfiguration } from '@/services/models/KeycloakConfiguration'
import bus from '@/helper/Bus'
import { MissingAccessory } from '@/services/models/MissingAccessory.ts'
import { FeedbackSurveyResponse } from '@/services/models/FeedbackSurveyResponse.ts'

export interface FetchResponse<T = any> {
  data: T
  status: number
  statusText: string
}

class ApiService {
  private projectCitiesAbortController?: AbortController
  private customerCitiesAbortController?: AbortController

  public readonly BASE_URL = '/api/'

  private static prepareHeader(addToken = true): Record<string, string> {
    const headers: Record<string, string> = {}

    if (addToken) {
      headers['Authorization'] = `Bearer ${userStore.token}`
    }

    if (userStore.forcedBrand) {
      headers['X-Brand'] = userStore.forcedBrand
    }

    if (userStore.forcedCountry) {
      headers['X-Country'] = userStore.forcedCountry
    }

    if (userStore.companyIdSuffix) {
      headers['X-CompanyId-Suffix'] = userStore.companyIdSuffix
    }

    if (userStore.planningSuiteToken) {
      headers['X-PS-Integration-Token'] = userStore.planningSuiteToken
    }

    headers['Accept-Language'] = userStore.currentIsoLocale ? userStore.currentIsoLocale : ''
    headers['Content-Type'] = 'application/json'

    return headers
  }

  private static async handleResponse<T = unknown>(response: Response): Promise<FetchResponse<T>> {
    const { status, statusText, ok } = response

    if (!ok) {
      if (status === 401) {
        bus.$emit(BusEvent.USER_LOGOUT)
        throw new Error('UNAUTHORIZED')
      } else {
        throw new Error(statusText)
      }
    }

    let data: unknown
    try {
      data = await response.json()
    } catch (e) {
      data = null
    }
    return { data, status, statusText } as FetchResponse<T>
  }

  public async doGet<T = unknown>(
    url: string,
    authenticate = true,
    method = 'GET',
    signal?: AbortSignal
  ): Promise<FetchResponse<T>> {
    const response = await fetch(`${this.BASE_URL}${url}`, {
      method,
      headers: ApiService.prepareHeader(authenticate),
      signal
    })

    return ApiService.handleResponse(response)
  }

  public doDelete<T = unknown>(url: string, authenticate = true): Promise<FetchResponse<T>> {
    return this.doGet<T>(url, authenticate, 'DELETE')
  }

  public async doPost<T = any>(url: string, body: unknown, method = 'POST'): Promise<FetchResponse<T>> {
    const headers = ApiService.prepareHeader()
    const response = await fetch(`${this.BASE_URL}${url}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null
    })

    return ApiService.handleResponse(response)
  }

  public doPut<T = any>(url: string, body: unknown): Promise<FetchResponse<T>> {
    return this.doPost<T>(url, body, 'PUT')
  }

  private doPatch<T = any>(url: string, body: unknown = null): Promise<FetchResponse<T>> {
    return this.doPost<T>(url, body, 'PATCH')
  }

  public async loadConfiguration(): Promise<ConfigurationResponse> {
    const res = await this.doGet<ConfigurationResponse>('configuration')
    return res.data
  }

  public async fetchKeycloakConfiguration(): Promise<KeycloakConfiguration> {
    const response = await fetch(`/keycloak/config`)
    return await response.json()
  }

  public async fetchProjects(
    page = 1,
    size = 15,
    sortBy = '',
    sortDirection = false,
    search = ''
  ): Promise<ProjectOverviewResponse> {
    try {
      const searchParams = new URLSearchParams()
      if (page) {
        searchParams.append('page', page.toString())
      }
      if (size) {
        searchParams.append('size', size.toString())
      }
      if (sortBy) {
        searchParams.append('sortBy', sortBy)
      }
      searchParams.append('sortDirection', sortDirection ? SortDirection.DESC : SortDirection.ASC)
      if (search) {
        searchParams.append('search', search)
      }

      const paramString = Array.from(searchParams).length ? '?' + searchParams : ''
      const res = await this.doGet<ProjectOverviewResponse>(`projects${paramString}`)

      return res.data
    } catch (e) {
      window.console.error('Error fetching projects from API:')
      throw Error((e as Error).message)
    }
  }

  public async loadProject(id: string): Promise<ProjectResponse> {
    try {
      const res = await this.doGet<ProjectResponse>(`projects/${id}`)
      return res.data
    } catch (e) {
      window.console.error('Error loading project from API:', e)
      throw Error((e as Error).message)
    }
  }

  public async createProject(projectData: ProjectDataModel): Promise<ProjectResponse> {
    try {
      const res = await this.doPost('projects', projectData)
      return res.data
    } catch (e) {
      window.console.error('Error creating project via API:')
      throw Error((e as Error).message)
    }
  }

  public async updateProject(id: string, projectData: ProjectDataModel): Promise<ProjectResponse> {
    try {
      const res = await this.doPut(`projects/${id}`, projectData)
      return res.data
    } catch (e) {
      window.console.error('Error updating project via API:')
      throw Error((e as Error).message)
    }
  }

  public async deleteProject(id: string): Promise<void> {
    try {
      await this.doDelete(`projects/${id}`)
    } catch (e) {
      window.console.error('Error deleting project via API:')
      throw Error((e as Error).message)
    }
  }

  public async loadSystemSchemes(id: string): Promise<SchemeResponse[]> {
    try {
      const res = await this.doGet<SchemeResponse[]>(`projects/${id}/systemscheme`)
      return res.status === 204 ? [] : res.data
    } catch (e) {
      window.console.error('Error loading products:', (e as Error).message)
      return []
    }
  }

  public async loadProducts(id: string, type: ProductType, additionalParam?: string): Promise<ProductResponse[]> {
    try {
      const res = await this.doGet<ProductResponse[]>(`projects/${id}/${type}${additionalParam ? `?${additionalParam}` : ''}`)
      return res.status === 204 ? [] : res.data
    } catch (e) {
      window.console.error('Error loading products:', (e as Error).message)
      return []
    }
  }

  public async loadCombinables(characteristic: CharacteristicType, productId?: string): Promise<ProductResponse[]> {
    try {
      const projectId = projectStore.projectId
      let url = `projects/${projectId}/combinables?characteristic=${characteristic}`

      if (productId) {
        url += `&productId=${productId}`
      }

      const res = await this.doGet<ProductResponse[]>(url)

      return res.status === 200 ? res.data : []
    } catch (e) {
      window.console.error('Error loading products:', (e as Error).message)
      return []
    }
  }

  public async loadHybridKit(): Promise<ProductResponse[]> {
    try {
      const res = await this.doGet<ProductResponse[]>(`projects/${projectStore.projectId}/hybridKit`)
      return res.status === 200 ? res.data : []
    } catch (e) {
      window.console.error('Error loading hybrid kit:', (e as Error).message)
      return []
    }
  }

  public async loadMissingAccessories(selectedAccessories: { id: string, quantity: number }[]): Promise<MissingAccessory[]> {
    try {
      const res = await this.doPost(`missing-accessories`, selectedAccessories)
      return res.status === 200 ? res.data : []
    } catch (e) {
      window.console.error('Error requesting scheme:', (e as Error).message)
      throw Error((e as Error).message)
    }
  }

  public async loadProjectCities(postalCode: string): Promise<Record<string, string>[]> {
    try {
      this.projectCitiesAbortController?.abort()
      this.projectCitiesAbortController = new AbortController()

      const res = await this.doGet<Promise<Record<string, string>[]>>(
        `locations?postalCode=${encodeURIComponent(postalCode)}`,
        true,
        'GET',
        this.projectCitiesAbortController?.signal
      )

      this.projectCitiesAbortController = undefined

      return res.status === 204 ? [] : res.data
    } catch (e) {
      throw Error((e as Error).message)
    }
  }

  public async loadCustomerCities(postalCode: string): Promise<Record<string, string>[]> {
    try {
      this.customerCitiesAbortController?.abort()
      this.customerCitiesAbortController = new AbortController()

      const res = await this.doGet<Promise<Record<string, string>[]>>(
        `locations?postalCode=${encodeURIComponent(postalCode)}`,
        true,
        'GET',
        this.customerCitiesAbortController?.signal
      )

      this.customerCitiesAbortController = undefined

      return res.status === 204 ? [] : res.data
    } catch (e) {
      throw Error((e as Error).message)
    }
  }

  public async loadOutdoorTemperature(postalCode: string, city?: string, altitude?: string): Promise<any> {
    try {
      const res = await this.doGet<Promise<number>>(
        `outdoor-temperature?postalCode=${postalCode}&city=${city}&altitude=${altitude}`
      )

      return res.status === 204 ? undefined : res.data
    } catch (e) {
      throw Error((e as Error).message)
    }
  }

  public async openReportInBrowser(id: string, variants: ReportVariant[]) {
    const baseUrl = await this.buildPdfUrl(id, variants)

    window.open(`${baseUrl}&token=${userStore.token}`, '_blank')
  }

  public async buildPdfUrl(id: string, variants: Array<ReportVariant>, integrationUrl: string = '') {

    let localeOverrides: Record<string, string> = {}

    if (userStore.forcedBrand) {
      const keycloakConfiguration = await this.fetchKeycloakConfiguration()

      const subBrand = keycloakConfiguration.realmMapping
        .find(rm => rm.brand === userStore.forcedBrand && rm.country === userStore.forcedCountry)
        ?.subBrand

      localeOverrides = {
        'brand': subBrand || userStore.forcedBrand,
        'country': userStore.forcedCountry,
        'language': userStore.currentIsoLocale.split('-')[0]
      }
    }

    const params = buildURLQueryParams({
      variants: variants.join(','),
      showPricesInstaller: projectStore.showPrices.toString(),
      showPricesCustomer: projectStore.showPricesCustomer.toString(),
      extendedNoiseCalculation: projectStore.showExtendedNoiseValues.toString(),
      ...localeOverrides
    })

    const baseUrl = integrationUrl || `${this.BASE_URL}projects/${id}/report`

    return `${baseUrl}${params}`
  }

  public async downloadProject(id: string, variants: Array<ReportVariant>): Promise<void> {
    const url = await this.buildPdfUrl(id, variants)
    await this.downloadUrl(url)
  }

  public async downloadUrl(url: string) {
    const fetchRes: Response = await fetch(url, { headers: ApiService.prepareHeader(true) })
    if (!fetchRes.ok) {
      throw new Error('Download failed: ' + fetchRes.statusText)
    }

    try {
      const res = await fetchRes.blob()
      const contentDispositionHeader = fetchRes.headers.get('content-disposition') || 'filename*=UTF-8\'\''
      const headerParts = contentDispositionHeader.split('filename*=UTF-8\'\'')
      const fileName = decodeURIComponent(headerParts[1])

      this.downloadBlob([res], fetchRes.headers.get('content-type') || '', fileName)
    } catch (e) {
      window.console.error('Download failed with error', e)
      throw Error((e as Error).message)
    }
  }

  public downloadBlob(blobData: BlobPart[], contentType: string, fileName: string) {
    const blob = new Blob(blobData, { type: contentType })

    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName)
      return
    }

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    link.click()
    window.setTimeout(() => {
      URL.revokeObjectURL(link.href)
    }, 100)
  }

  public async callProjectSimulation(id: string): Promise<boolean> {
    const url = `projects/${id}/simulation`
    let res: any = false

    try {
      res = await this.doPatch(url)
    } catch (e) {
      window.console.error('Simulation call failed:', (e as Error).message)
      return false
    }

    return res.status < 300
  }

  public async requestScheme(id: string, data: SchemeRequest): Promise<void> {
    try {
      await this.doPost(`projects/${id}/requestscheme`, data)
    } catch (e) {
      window.console.error('Error requesting scheme:', (e as Error).message)
      throw Error((e as Error).message)
    }
  }

  public async calculateCoolingLoad(data: CalculatedCoolingLoadRequest): Promise<number | null> {
    try {
      const res = await this.doPost(`coolingload`, data)
      return res.status === 200 ? res.data?.coolingLoad : null
    } catch (e) {
      window.console.error('Error calculating cooling load via API:')
      throw Error((e as Error).message)
    }
  }

  public async calculateHeatingLoad(data: CalculatedHeatingLoadRequest): Promise<number | null> {
    try {
      const res = await this.doPost(`heatingload`, data)
      return res.status === 200 ? res.data?.heatingLoad : null
    } catch (e) {
      window.console.error('Error calculating heating load via API:')
      throw Error((e as Error).message)
    }
  }

  public async loadConfigSchema(): Promise<any> {
    try {
      const res = await this.doGet<any>('admin/nscconfig/schema')
      return res.data
    } catch (e) {
      window.console.error('Error loading schema config:')
      throw Error((e as Error).message)
    }
  }

  public async loadAdminConfig(brand: string, country: string): Promise<any> {
    try {
      const res = await this.doGet<any>(`admin/nscconfig/${brand}/${country}`)
      return res.data
    } catch (e) {
      window.console.error('Error loading admin config:')
      throw Error((e as Error).message)
    }
  }

  public async updateAdminConfig(brand: string, country: string, configData: any): Promise<any> {
    try {
      const res = await this.doPut(`admin/nscconfig/${brand}/${country}`, configData)
      return res.data
    } catch (e) {
      window.console.error('Error updating admin config:')
      throw Error((e as Error).message)
    }
  }

  public async loadAvailableAdminConfigs(): Promise<AvailableConfigsType> {
    try {
      const res = await this.doGet<AvailableConfigsType>(`admin/nscconfig`)
      return res.data
    } catch (e) {
      window.console.error('Error loading available admin configs:')
      throw Error((e as Error).message)
    }
  }

  public async loadAvailableAdminTranslations(): Promise<AvailableTranslationType[]> {
    try {
      return (await this.doGet<AvailableTranslationType[]>(`admin/nsctranslation`)).data
    } catch (e) {
      window.console.error('Error loading available admin translations:')
      throw Error((e as Error).message)
    }
  }

  public async loadAdminTranslations(brand: string, locale: string): Promise<any> {
    try {
      return (await this.doGet<any>(`admin/nsctranslation/${brand}/${locale}`)).data
    } catch (e) {
      window.console.error('Error loading admin translation:')
      throw Error((e as Error).message)
    }
  }

  public async updateAdminTranslations(brand: string, locale: string, translation: any): Promise<any> {
    try {
      return (await this.doPut(`admin/nsctranslation/${brand}/${locale}`, translation)).data
    } catch (e) {
      window.console.error('Error updating admin translation:')
      throw Error((e as Error).message)
    }
  }

  public async updateProjectPrices(id: string): Promise<any> {
    try {
      return (await this.doPost(`projects/${id}/update-prices`, {})).data
    } catch (e) {
      window.console.error('Error updating project prices:')
      throw Error((e as Error).message)
    }
  }

  public async updateOutdoorTemperature(file: File): Promise<Response> {
    try {
      const headers = ApiService.prepareHeader()
      delete headers['Content-Type']
      const data = new FormData()
      data.append('file', file)

      return await fetch(`${this.BASE_URL}admin/outdoor-temperatures/xlsx`, {
        method: 'POST',
        headers: headers,
        body: data
      })
    } catch (e) {
      console.error('Error updating outdoor temperatures')
      throw Error((e as Error).message)
    }
  }

  public async downloadOutdoorTemperature(country: string): Promise<any> {
    try {
      const response = await fetch(`${this.BASE_URL}admin/outdoor-temperatures/xlsx/${country.toUpperCase()}`, {
        method: 'GET',
        headers: ApiService.prepareHeader()
      })
      const blob = await response.blob()
      this.downloadBlob([blob], response.headers.get('content-type') || '', `outdoor-temperatures-${country}.xlsx`)
    } catch (e) {
      console.error('Error updating outdoor temperatures')
      throw Error((e as Error).message)
    }
  }

  public async downloadHeatPumpExcel(projectId: string, type: DownloadExcelType, fileName: string, heatPumpId: string, installationType: InstallationType, cascadeSize: number = 1): Promise<any> {
    const response = await fetch(`${this.BASE_URL}admin/projects/${projectId}/heatpump/${type}?heatPumpId=${heatPumpId}&cascadeSize=${cascadeSize}&type=${installationType}`, {
      method: 'GET',
      headers: ApiService.prepareHeader()
    })

    if (response.ok) {
      const blob = await response.blob()
      this.downloadBlob([blob], response.headers.get('content-type') || '', `${fileName}-${heatPumpId}.xlsx`)
    } else {
      const errorMessage = await response.json()
      alert(`An error occurred while trying to calculate the data. ${errorMessage.message}`)
      throw Error(errorMessage.message)
    }
  }

  async explainSystemScheme(projectId: string, schemeFileName: string): Promise<string[]> {
    try {
      const res = await this.doGet<string[]>(`projects/${projectId}/systemscheme/explain/${schemeFileName}`)
      return res.data
    } catch (e) {
      window.console.error('Error explaining system scheme query:', e)
      throw Error((e as Error).message)
    }
  }

  async importProducts(reimportPictures: boolean) {
    try {
      const res = await this.doPost<string[]>(`admin/import/products?reimportPictures=${reimportPictures}`, '{}')
      return res.data
    } catch (e) {
      window.console.error('Error importing products:', e)
      throw Error((e as Error).message)
    }
  }

  async importSystemSchemes(forceReimport: boolean) {
    try {
      const res = await this.doPost<string[]>(`admin/import/systemschemes?forceReimport=${forceReimport}`, '{}')
      return res.data
    } catch (e) {
      window.console.error('Error importing system schemes:', e)
      throw Error((e as Error).message)
    }
  }

  async downloadProductCatalogDump() {
    try {
      await this.downloadUrl(`${this.BASE_URL}admin/product/catalog`)
    } catch (e) {
      console.error('Error downloading product catalog dump')
      throw Error((e as Error).message)
    }
  }

  async shouldShowFeedbackSurvey(): Promise<boolean> {
    try {
      const res = await this.doGet<FeedbackSurveyResponse>(`feedback/`)
      return res.status === 204 ? false : res.data.showSurvey
    } catch (e) {
      window.console.error('An error occurred while checking if survey should be visible :', e)
      throw Error((e as Error).message)
    }
  }

}

export const api = new ApiService()
